import {
DefaultButton,
Dialog,
DialogFooter,
DialogType,
    IDialogProps
} from "@fluentui/react";
import { Observer } from "mobx-react";
import { AriaProperties } from "owa-accessibility";
import * as React from "react";
import AutomationUtils from "sh-application/utility/AutomationUtil";
import StringsStore from "sh-strings/store";

import { IPartialShiftsDialogStyles, useShiftsDialogClassNames } from "./ShiftsDialog.styles";

export interface IShiftDialogProps extends IDialogProps {
    /** (optional) dialog content to be rendered */
    dialogContent?: React.ReactNode;

    /** Is the dialog blocking (includes blocking overlay) - default true */
    isBlocking?: boolean;

    /** Is the dialog hidden - default false */
    isHidden?: boolean;

    /** (optional) If true, the close 'x' button in the dialog is shown */
    showCloseButton?: boolean;

    /** (optional) Title for the dialog */
    title?: string;

    /** (optional) Sub text to display in the dialog */
    subText?: string;

    /** (optional) If true, the ok button is disabled */
    disableOkayButton?: boolean;

    /** (optional) If true, the ok button is disabled */
    disableApplyButton?: boolean;

    /** (optional) Callback that is invoked when the user clicks the cancel button */
    onCancelCallback?: () => void;

    /** (optional) Callback that is invoked when the user clicks the ok button */
    onOkCallback?: () => void;

    /** (optional) Callback that is invoked when the user dismisses the dialog */
    onDismissCallback?: () => void;

    /** (optional) Callback that is invoked when the user clicks the apply button */
    onApplyCallback?: () => void;

    /** (optional) Custom label to use on the ok button in the footer - default is ok */
    okText?: string;

    /** (optional) Custom label to use on the cancel button in the footer - default is Cancel */
    cancelText?: string;

    /** (optional) Custom label to use on the apply button in the footer - default is Apply */
    applyText?: string;

    /** (optional) If true, the action buttons are hidden */
    hideActionButtons?: boolean;

    /** (optional) If true, the ok button is hidden */
    hideOkButton?: boolean;

    /** (optional) If true, apply button is shown*/
    showApplyButton?: boolean;

    /** (optional) If true, the cancel button is hidden */
    hideCancelButton?: boolean;

    /** optionally render ok button icon*/
    onRenderOkButtonIcon?: () => JSX.Element;

    /** (optional) Aria properties for the Apply button */
    applyButtonAriaProps?: AriaProperties;

    /**(optional) change styling of ok button to default button styling - used to maintain focus without switching buttons but just changing style*/
    changeOkButtonStyleToDefault?: boolean;

    /**(optional) allows disabled buttons to remain focusable */
    allowDisableFocus?: boolean;

    /** (optional) determines where to place the first focus. If no value is passed defaults to setting focus on the 'x' button in the dialog */
    firstFocusableSelector?: string;

    /** (optional) theme overrides for the dialog. */
    themeOverrides?: IPartialShiftsDialogStyles;

    /**
     * (optional) The ID for the element. Used for screen reader accessibility by assigning the Dialog's Aria-describedby to the element with this ID.
     */
    subtitleAriaId?: string;

    /**
     * (optional) The ID for the element. Used for screen reader accessibility by assigning the Dialog's Aria-labeledby to the element with this ID.
     */
    titleAriaId?: string;

    /**
     * (optional) Classname for the dialog
     */
    className?: string;

    /**
     * (optional) Classname for the cancel button
     */
    cancelButtonClassName?: string;

    /**
     * (optional) Classname for the ok button
     */
    okButtonClassName?: string;

    /**
     * (optional) Classname for the apply button
     */
    applyButtonClassName?: string;
}

const classNames = require("classnames/bind");
const okButtonId = "shiftsDialogOkButton";

/**
 * A wrapper component for shift dialogs
 */
export function ShiftsDialog(props: IShiftDialogProps) {
    const _strings = StringsStore().registeredStringModules.get("common").strings;
    const {
        cancelText,
        changeOkButtonStyleToDefault,
        disableOkayButton,
        disableApplyButton,
        isHidden = false,
        okText,
        applyText,
        isBlocking = true,
        className,
        title,
        allowDisableFocus,
        subText,
        showCloseButton,
        themeOverrides,
        subtitleAriaId,
        titleAriaId,
        okButtonClassName
    } = props;
    let { applyButtonAriaProps } = props;
    const classes = useShiftsDialogClassNames(themeOverrides);
    const cancelButtonLabel: string = cancelText ? cancelText : _strings.get("cancel");
    const okButtonLabel: string = okText ? okText : _strings.get("ok");
    const applyButtonLabel: string = applyText ? applyText : _strings.get("apply");
    applyButtonAriaProps = applyButtonAriaProps || ({} as AriaProperties);
    applyButtonAriaProps.label = applyButtonAriaProps.label || applyButtonLabel;

    let cancelButtonClassNames =
        props.hideActionButtons || props.hideCancelButton
            ? classNames(classes.buttonSpacing, classes.hideButton)
            : classNames(classes.buttonSpacing);
    let applyButtonClassNames =
        props.hideActionButtons || !props.showApplyButton
            ? classNames(classes.buttonSpacing, classes.hideButton)
            : classNames(classes.buttonSpacing);
    let okButtonClassNames = classNames(okButtonClassName);
    if (props.hideActionButtons || props.hideOkButton) {
        okButtonClassNames = classNames(classes.hideButton);
    }

    React.useEffect(() => {
        // Save the active element to return focus when modal is closed.
        const activeElement = document.activeElement;

        return () => {
            // Return focus to the previously focused element
            const element = activeElement as HTMLElement;
            clearAriaHidden(element);
            element.focus();
        };
      }, []);

    const onSubmit = (evt: React.FormEvent<HTMLFormElement>) => {
        evt.preventDefault();
        if (props.onOkCallback && !props.disableOkayButton) {
            props.onOkCallback();
        }
    };

    const onDismissDialog = () => {
        if (props.onDismissCallback) {
            props.onDismissCallback();
        }
    };

    const clearAriaHidden = (element: HTMLElement) => {
        let ancestor = element;
            while (ancestor) {
                if (ancestor.getAttribute && ancestor.getAttribute('aria-hidden') === 'true') {
                    ancestor.setAttribute('aria-hidden', 'false');
                    break;
                }
                ancestor = ancestor.parentElement;
            }
      };

    return (
        <Observer>
            {() => (
                <Dialog
                    hidden={isHidden}
                    firstFocusableSelector={props.firstFocusableSelector}
                    onDismiss={onDismissDialog}
                    dialogContentProps={{
                        type: DialogType.normal,
                        title: title,
                        subText: subText,
                        showCloseButton
                    }}
                    modalProps={{
                        titleAriaId,
                        isBlocking: !!isBlocking,
                        className: classNames(className),
                        scrollableContentClassName: classes.scrollableContent,
                        subtitleAriaId // Assigns the Dialog's Aria-describedby based on the ariaId passed in.
                    }}
                >
                    {/**
                     * We have the form so that 'enter' in input fields
                     * will trigger the 'ok' confirm action
                     * Unless the actions buttons are present, this should not render a Form, it is not semantically correct
                     * to render the form in the Third Party Notice that has not input's for example.
                     * Rendering a fragment for those cases instead.
                     **/}
                    {props.hideActionButtons ? (
                        props.dialogContent
                    ) : (
                        <form onSubmit={onSubmit}>{props.dialogContent}</form>
                    )}
                    {!props.hideActionButtons && (
                        <DialogFooter>
                            <DefaultButton
                                className={cancelButtonClassNames}
                                data-automation-id={AutomationUtils.getAutomationId(
                                    "dialog",
                                    "QAIDDialogCancelButton"
                                )}
                                onClick={props.onCancelCallback}
                                allowDisabledFocus={!!allowDisableFocus} // Allows button to remain focusable if disabled.
                                ariaLabel={cancelButtonLabel}
                            >
                                {cancelButtonLabel}
                            </DefaultButton>
                            <DefaultButton
                                className={applyButtonClassNames}
                                data-automation-id={AutomationUtils.getAutomationId(
                                    "dialog",
                                    "QAIDDialogApplyButton"
                                )}
                                onClick={props.onApplyCallback}
                                disabled={disableApplyButton}
                                ariaLabel={applyButtonAriaProps.label}
                            >
                                {applyButtonLabel}
                            </DefaultButton>
                            <DefaultButton
                                primary={!changeOkButtonStyleToDefault}
                                id={okButtonId}
                                className={okButtonClassNames}
                                data-automation-id={AutomationUtils.getAutomationId(
                                    "dialog",
                                    "QAIDDialogOkayButton"
                                )}
                                onClick={props.onOkCallback}
                                disabled={disableOkayButton}
                                allowDisabledFocus={!!allowDisableFocus} // Allows button to remain focusable if disabled.
                                ariaLabel={okButtonLabel}
                            >
                                {props.onRenderOkButtonIcon && props.onRenderOkButtonIcon()}
                                {okButtonLabel}
                            </DefaultButton>
                        </DialogFooter>
                    )}
                </Dialog>
            )}
        </Observer>
    );
}
